/** Languages used for admin field labels in the BO (not content-only locales like DE). */
export const BO_LABEL_FALLBACK_LANGS = ['fr', 'en'] as const

export function normalizeLang(lang?: string): string {
  return String(lang || '')
    .trim()
    .toLowerCase()
}

export function compactTranslations<T>(translations?: T[] | null): T[] {
  return (translations || []).filter(Boolean) as T[]
}

export function findTranslationIndex(
  translations: { language?: string }[] = [],
  lang: string,
): number {
  const lc = normalizeLang(lang)
  return compactTranslations(translations).findIndex(
    (t) => normalizeLang(t?.language) === lc,
  )
}

export function pickContentTranslation<
  T extends { language?: string; value?: string; title?: string },
>(translations: T[] = [], lang: string): T | undefined {
  const lc = normalizeLang(lang)
  return compactTranslations(translations).find((t) => normalizeLang(t.language) === lc)
}

/** Admin label for an attribute — stable across locales; falls back to FR/EN then attribute key. */
export function pickAttributeLabel(
  translations: { language?: string; label?: string }[] = [],
  attributeKey?: string,
): string {
  const rows = compactTranslations(translations)
  for (const lang of BO_LABEL_FALLBACK_LANGS) {
    const label = rows.find((t) => normalizeLang(t.language) === lang)?.label?.trim()
    if (label) return label
  }
  const deLabel = rows.find((t) => normalizeLang(t.language) === 'de')?.label?.trim()
  if (deLabel) return deLabel
  const any = rows.find((t) => t.label?.trim())
  if (any?.label?.trim()) return any.label.trim()
  const key = attributeKey?.trim()
  if (key) return key
  return 'Untitled'
}

/** Content field (value/title) with locale fallback like production FO. */
export function pickContentField(
  translations: { language?: string; value?: string; title?: string }[] = [],
  lang: string,
  field: 'value' | 'title',
  fallback = '',
): string {
  if (!translations.length) return fallback
  const lc = lang.trim().toLowerCase()
  const order = [lc, ...BO_LABEL_FALLBACK_LANGS.filter((code) => code !== lc), 'de']
  for (const code of order) {
    const row = translations.find(
      (t) => String(t.language ?? '').trim().toLowerCase() === code,
    )
    const text = row?.[field]?.trim()
    if (text) return text
  }
  const any = translations.find((t) => t[field]?.trim())
  return any?.[field]?.trim() ?? fallback
}

/** Display label for themes/options/facilities — active locale then FR/EN fallback. */
export function pickTranslatedLabel(
  translations:
    | { language?: string; title?: string; name?: string; label?: string }[]
    | undefined,
  lang: string,
  fallback?: string,
): string {
  if (!translations?.length) return fallback ?? ''
  const lc = lang.trim().toLowerCase()
  const order = [lc, ...BO_LABEL_FALLBACK_LANGS.filter((code) => code !== lc)]
  for (const code of order) {
    const t = translations.find(
      (row) => String(row.language ?? '').trim().toLowerCase() === code,
    )
    const label = (t?.title ?? t?.name ?? t?.label)?.trim()
    if (label) return label
  }
  const any = translations.find((t) => (t.title ?? t.name ?? t.label)?.trim())
  return (any?.title ?? any?.name ?? any?.label ?? fallback ?? '').trim()
}

export function pickLocalizedButtonLink(
  translations: { language?: string; buttonLink?: string }[] = [],
  lang: string,
): string {
  const lc = normalizeLang(lang)
  const row = compactTranslations(translations).find(
    (t) => normalizeLang(t.language) === lc,
  )
  return String(row?.buttonLink ?? '').trim()
}

export function upsertTranslationField(
  translations: {
    _id?: string
    language?: string
    label?: string
    value?: string
    title?: string
    buttonLink?: string
  }[],
  lang: string,
  field: 'value' | 'title' | 'buttonLink',
  fieldValue: string,
  labelFallback = '',
): typeof translations {
  const idx = findTranslationIndex(translations, lang)
  if (idx >= 0) {
    return translations.map((t, i) => (i === idx ? { ...t, [field]: fieldValue } : t))
  }
  return [
    ...translations,
    {
      language: lang,
      label: labelFallback,
      value: field === 'value' ? fieldValue : '',
      title: field === 'title' ? fieldValue : '',
      buttonLink: field === 'buttonLink' ? fieldValue : '',
    },
  ]
}
